import type { JobFiltersState } from '../components/JobFilters'
import type { LocationSuggestion } from '../hooks/useLocationSearch'

/**
 * Convert a string to URL-safe kebab-case
 */
export function toKebabCase(str: string): string {
    return str
        .toLowerCase()
        .replace(/\s+/g, '-')
        .replace(/[^a-z0-9-]/g, '')
}

/**
 * Convert kebab-case back to title case
 * customer-success → Customer Success
 */
export function fromKebabCase(str: string): string {
    return str
        .split('-')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
}

/**
 * Convert filters to URL path segments
 * Returns an array of path segments like ['remote', 'engineering', 'senior']
 */
export function filtersToPath(filters: JobFiltersState): string[] {
    const segments: string[] = []

    // Add work types (remote, hybrid, onsite)
    if (filters.workTypes.size > 0) {
        filters.workTypes.forEach(workType => {
            segments.push(toKebabCase(workType))
        })
    }

    // Add teams/job families
    if (filters.teams.size > 0) {
        filters.teams.forEach(team => {
            segments.push(toKebabCase(team))
        })
    }

    // Add seniority levels
    if (filters.seniorities.size > 0) {
        filters.seniorities.forEach(seniority => {
            segments.push(toKebabCase(seniority))
        })
    }

    // Add stages with /stage/ prefix
    if (filters.stages.size > 0) {
        filters.stages.forEach(stage => {
            segments.push('stage', toKebabCase(stage))
        })
    }

    // Add location (city, state, or country name)
    if (filters.selectedLocation) {
        segments.push(toKebabCase(filters.selectedLocation.name))
    }

    return segments
}

/**
 * Parse URL path segments back to filters
 * Takes array of path segments and returns a JobFiltersState object
 */
export function pathToFilters(segments: string[]): Partial<JobFiltersState> {
    const filters: Partial<JobFiltersState> = {
        workTypes: new Set(),
        seniorities: new Set(),
        teams: new Set(),
        roleAttributes: new Set(),
        compensation: new Set(),
        stages: new Set(),
        hasSalary: false,
        selectedLocation: null
    }

    // Known filter values for matching
    const workTypeValues = ['remote', 'hybrid', 'onsite']
    const seniorityValues = ['internship', 'entry', 'mid', 'senior', 'lead', 'principal']

    // Process segments, looking ahead for /stage/ prefix
    for (let i = 0; i < segments.length; i++) {
        const segment = segments[i]
        const normalized = segment.toLowerCase()

        // Check if this is a stage prefix
        if (normalized === 'stage' && i + 1 < segments.length) {
            // Next segment is the stage value
            const stageValue = segments[i + 1].toLowerCase()
            filters.stages?.add(stageValue)
            i++ // Skip the next segment since we've processed it
            continue
        }

        // Check if it's a work type
        if (workTypeValues.includes(normalized)) {
            filters.workTypes?.add(normalized)
        }
        // Check if it's a seniority level
        else if (seniorityValues.includes(normalized)) {
            filters.seniorities?.add(normalized)
        }
        // Otherwise, assume it's a team/job family or location
        // We'll treat it as a team for now - location will be handled separately
        // because we need the full LocationSuggestion object with coordinates
        else {
            // Convert back from kebab-case
            const teamName = fromKebabCase(segment)
            filters.teams?.add(teamName)
        }
    }

    return filters
}

/**
 * Generate the full URL path from filters
 */
export function generateUrlPath(filters: JobFiltersState): string {
    const segments = filtersToPath(filters)
    if (segments.length === 0) {
        return '/'
    }
    return '/' + segments.join('/')
}

/**
 * Check if a segment could be a location name
 * This is a heuristic - we'll check against available teams to disambiguate
 */
export function isLikelyLocation(segment: string, availableTeams: string[]): boolean {
    const normalized = fromKebabCase(segment)

    // If it matches a known team exactly, it's probably a team
    const matchesTeam = availableTeams.some(team =>
        team.toLowerCase() === normalized.toLowerCase()
    )

    if (matchesTeam) {
        return false
    }

    // Known work types, seniorities, and special keywords are not locations
    const workTypeValues = ['remote', 'hybrid', 'onsite']
    const seniorityValues = ['internship', 'entry', 'mid', 'senior', 'lead', 'principal']
    const specialKeywords = ['stage']

    if (workTypeValues.includes(segment.toLowerCase()) ||
        seniorityValues.includes(segment.toLowerCase()) ||
        specialKeywords.includes(segment.toLowerCase())) {
        return false
    }

    // If it doesn't match any known filter type, assume it's a location
    return true
}
